import { Copy, Check } from 'lucide-react'
import React, { useState, useCallback, ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface CopyableContentProps {
  content: string
  children: ReactNode
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  className?: string
  buttonClassName?: string
  showOnHover?: boolean
  disabled?: boolean
  copyType?: 'text' | 'html' | 'both'
  htmlContent?: string // For when copying HTML format
  'data-testid'?: string
}

/**
 * CopyableContent - A wrapper component that adds copy functionality to any content
 * 
 * Features:
 * - Copy button with configurable positioning
 * - Text, HTML, or both clipboard formats
 * - Show on hover or always visible
 * - Toast feedback and visual feedback
 * - Accessible and keyboard friendly
 */
export const CopyableContent: React.FC<CopyableContentProps> = ({
  content,
  children,
  position = 'top-right',
  className,
  buttonClassName,
  showOnHover = true,
  disabled = false,
  copyType = 'text',
  htmlContent,
  'data-testid': testId
}) => {
  const [copyFeedback, setCopyFeedback] = useState<string>('')
  const [isHovered, setIsHovered] = useState(false)

  // Copy to clipboard functionality
  const handleCopy = useCallback(async () => {
    if (!content.trim()) return

    try {
      if (copyType === 'both' && htmlContent) {
        // Copy both HTML and text
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([content], { type: 'text/plain' }),
          }),
        ])
        toast.success('Content copied with formatting')
      } else if (copyType === 'html' && htmlContent) {
        // Copy HTML only
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
          }),
        ])
        toast.success('HTML content copied')
      } else {
        // Copy text only
        await navigator.clipboard.writeText(content)
        toast.success('Content copied to clipboard')
      }

      setCopyFeedback('Copied!')
      
      // Clear feedback after 2 seconds
      setTimeout(() => setCopyFeedback(''), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      setCopyFeedback('Failed')
      toast.error('Failed to copy to clipboard')
      setTimeout(() => setCopyFeedback(''), 2000)
    }
  }, [content, copyType, htmlContent])

  // Calculate copy button position styles
  const getCopyButtonStyles = useCallback(() => {
    const baseStyles = {
      position: 'absolute' as const,
      zIndex: 10,
      height: 28,
      width: 28,
      padding: 4,
    }

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: 8, left: 8 }
      case 'bottom-right':
        return { ...baseStyles, bottom: 8, right: 8 }
      case 'bottom-left':
        return { ...baseStyles, bottom: 8, left: 8 }
      default: // top-right
        return { ...baseStyles, top: 8, right: 8 }
    }
  }, [position])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const shouldShowButton = !disabled && content.trim() && (!showOnHover || isHovered || copyFeedback)

  return (
    <div 
      className={cn('relative', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={testId}
    >
      {children}
      
      {shouldShowButton && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopy}
          disabled={disabled || !content.trim()}
          style={getCopyButtonStyles()}
          title="Copy to clipboard"
          className={cn(
            'transition-all duration-200 bg-white/90 backdrop-blur-sm border-gray-300',
            'hover:bg-white hover:border-gray-400',
            showOnHover && !copyFeedback && !isHovered && 'opacity-0',
            buttonClassName
          )}
        >
          {copyFeedback ? (
            copyFeedback === 'Copied!' ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <span className="text-xs text-red-600">!</span>
            )
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  )
}

export default CopyableContent
